import {
  Field as RffField,
  FieldProps as RffFieldProps,
  FieldRenderProps,
} from "react-final-form";
import { FieldValidator } from "final-form";
import { useIntl } from "react-intl";
import { pick } from "lodash";
import type { IntlFormatters } from "@formatjs/intl";
import linkifyIt from "linkify-it";
import tlds from "tlds";

const linkify = linkifyIt();
linkify.tlds(tlds);

const httpLinkify = new linkifyIt()
  .set({ fuzzyLink: false })
  .add("ftp:", null)
  .add("//", null)
  .add("mailto:", null);

const emailRE = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;
const strongPasswordRE = new RegExp("^(?=.*[a-z])(?=.*[A-Z])(?=.{8,})");
const urlWithHttp = /^(?:https?)/;

type T = IntlFormatters["formatMessage"];

export const Validators = {
  composeValidators: (
    ...validators: FieldValidator<any>[]
  ): FieldValidator<any> => (value, values, fieldState) =>
    validators.reduce(
      (error, validator) => error || validator(value, values, fieldState),
      undefined
    ),
  required: (t: T): FieldValidator<any> => (val) =>
    val === null || val === undefined || val === ""
      ? t({ id: "requiredLabel" })
      : undefined,
  requiredWith: (t: T, fields: string[]): FieldValidator<any> => (
    val,
    allValues
  ) =>
    !val && Object.values(pick(allValues, fields)).find((v) => !!v)
      ? t({ id: "requiredLabel" })
      : undefined,
  isEmail: (t: T): FieldValidator<string> => (val) =>
    val && !emailRE.test(val) ? t({ id: "invalidEmailError" }) : undefined,
  minLength: (t: T, minLen: number): FieldValidator<string> => (val) =>
    val.length < minLen ? t({ id: "tooShortError" }, { minLen }) : undefined,
  isPassword: (t: T): FieldValidator<string> => (val) =>
    val && !strongPasswordRE.test(val)
      ? t({ id: "invalidPasswordError" })
      : undefined,
  isUrl: (t: T): FieldValidator<string> => (val) =>
    val && !linkify.test(val) ? t({ id: "invalidUrlError" }) : undefined,
  isHttpUrl: (t: T): FieldValidator<string> => (val) =>
    val && !httpLinkify.test(val) ? t({ id: "invalidUrlError" }) : undefined,
  isDate: (t: T): FieldValidator<string> => (val: any) =>
    val && isNaN(val) && val instanceof Date
      ? t({ id: "invalidDate" })
      : undefined,
  isUrlWithoutHttp: (t: T): FieldValidator<string> => (val) =>
    val && urlWithHttp.test(val) ? t({ id: "urlWithHttpError" }) : undefined,
};

export type FieldProps = {
  name: string;
  required?: boolean;
  requiredWith?: string[];
  isPassword?: boolean;
  minLength?: number;
  isEmail?: boolean;
  isUrl?: boolean;
  isHttpUrl?: boolean;
  isDate?: boolean;
  isUrlWithoutHttp?: boolean;
  customValidator?: FieldValidator<any>;
};

const Field = ({
  children,
  required: isRequired,
  requiredWith,
  isPassword,
  minLength,
  isEmail,
  isUrl,
  isHttpUrl,
  isDate,
  isUrlWithoutHttp,
  customValidator,
  ...props
}: FieldProps & RffFieldProps<any, FieldRenderProps<any>>) => {
  const { formatMessage: t } = useIntl();

  const validators: FieldValidator<any>[] = [];
  if (isRequired) validators.push(Validators.required(t));
  if (requiredWith) validators.push(Validators.requiredWith(t, requiredWith));
  if (isEmail) validators.push(Validators.isEmail(t));
  if (minLength) validators.push(Validators.minLength(t, minLength));
  if (isPassword) validators.push(Validators.isPassword(t));
  if (isUrl) validators.push(Validators.isUrl(t));
  if (isHttpUrl) validators.push(Validators.isHttpUrl(t));
  if (isDate) validators.push(Validators.isDate(t));
  if (isUrlWithoutHttp) validators.push(Validators.isUrlWithoutHttp(t));
  if (customValidator) validators.push(customValidator);

  const validate =
    validators.length === 0
      ? undefined
      : validators.length > 1
      ? Validators.composeValidators(...validators)
      : validators[0];

  return <RffField {...{ ...props, validate }}>{children}</RffField>;
};

export default Field;
